import React, { useImperativeHandle, useRef, useState } from "react";
import { ExtendedNodeData, RCCAlert, RCCButton, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCRefHierarchicalTree, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTooltip, toggleExpandedForAll } from "react-common-components";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import Title from "src/components/Title";
import LovSubsidiary from "src/components/Lov/Subsidiary";
import { useForm } from "react-hook-form";
import LovUsers from "src/components/Lov/Users";
import LovUsersGroups from "src/components/Lov/UsersGroups";
import { standardErrorMessages } from "src/utils/errors";
import Select from "src/components/Select";
import { getTitleBudgetTypes } from "src/pages/FinancialManagement/interface";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import Loader from "src/components/Loader";
import IModalTotalRefPermissionsFinancialManagement, { IModalPermissionsTotalFinancialManagement } from "./interface";
import * as HierarchyStyles from "../styles";

const HierarchyNode: React.FC<ExtendedNodeData> = ({
	node,
	onSubmit,
	control
}) => {
	const {translate} = useTranslation();
	return(
		<HierarchyStyles.HierarchyDivContainer>
			
			<HierarchyStyles.Title>
				{node.name}
			</HierarchyStyles.Title> 
			<HierarchyStyles.StyledDiv >
				<Select 
					control={control}
					onChangeCapture={(e: any) => onSubmit(node.id, e === null ? "" : e.value)}
					name="permission"
					label=""
					options={[
						{
							value: "2",
							label: translate("Visualiza")
						},
						{
							value: "1",
							label: translate("Visualiza e altera valores")
						}
					]}
					isClearable
					defaultValue={node.permission}
					styleInline="height: 34px; min-height: 34px; margin: 0; padding: 0; width: 14rem;"
				/>		
			</HierarchyStyles.StyledDiv>
		</HierarchyStyles.HierarchyDivContainer>
	);};


const ModalPermissionsTotalFinancialManagement: React.ForwardRefRenderFunction<IModalTotalRefPermissionsFinancialManagement, IModalPermissionsTotalFinancialManagement> = ({
	type
}, ref) => {
	const { translate } = useTranslation(); 
	const { confirm } = useConfirmModal();
	const { addToast } = RCCHookToast();
	
	const [budgetPlanId, setBudgetPlanId] = useState(0);
	const modalPermissions = useRef<RCCRefModalProps>(null);
	const onCallbackFunction = useRef<Function>(() => {});
	
	const {control: controlPermission, reset: resetPermission} = useForm();
	const [loadingHierarchy, setLoadingHierarchy] = useState(false);
	const [hierarchy, setHierarchy] = useState<any[]>([]);

	const filterSwitch = useRef<boolean>(false);

	const getNodeKeyFunction = ({node}:any) => node.id;
	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const {control, register, reset} = useForm();

	const paramsOnPost = useRef({
		ofa002Id: "", 
		efa004Id: "", 
		efa005Id:  ""
	});

	const openModalPermissionsTotalFinancialManagement = async (ofa001Id: number, onCallback?: Function) => {
		setBudgetPlanId(ofa001Id);
		setHierarchy([]);
		filterSwitch.current = false;
		onCallbackFunction.current = onCallback || (() => {});
			
		modalPermissions.current?.openModal();	
	};
	const getHierarchy = async (ofa002Id?: string, efa004Id?: string, efa005Id?: string) => {
		try{
			resetPermission();
			setLoadingHierarchy(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetPermissionsTotal",
				urlParameters: {
					budgetPlanId, 
					ofa002Id: ofa002Id ?? "", 
					efa004Id: efa004Id ?? "", 
					efa005Id: efa005Id ?? "", 
					type, 
					filter: filterSwitch.current ?? false
				}
			});
			paramsOnPost.current = {
				ofa002Id: ofa002Id ?? "", 
				efa004Id: efa004Id ?? "", 
				efa005Id: efa005Id ?? ""
			};
			const expandedData = toggleExpandedForAll({treeData:[...data], expanded: true});
			setHierarchy(expandedData);			
			setLoadingHierarchy(false);
		}catch (err){
			setLoadingHierarchy(false);
			standardErrorMessages(err, translate("Erro ao buscar hierarquia"), addToast);
		}
	};
	const onSubmit = async (id: number, permission: string) => {
		try{
			await RCCServicesApi.postRequest({
				method: "FinancialManagement/InsertPermissionsTotal",
				urlParameters: {
					permission: permission ?? "",
					iten: id,
					budgetPlanId,
					type,
					...paramsOnPost.current
				}
			});
			addToast({
				title: translate("Sucesso"),
				description: translate("Permissão alterada com sucesso"),
				color: "green"
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao salvar permissões"), addToast);
		}
	};

	const deleteAllFromHierarchy = async() => {
		setHierarchy([]);
		setLoadingHierarchy(true);
		await RCCServicesApi.deleteRequest({
			method: "FinancialManagement/DeleteAllPermissionsTotal",
			urlParameters: {
				budgetPlanId, 
				ofa002Id: paramsOnPost.current.ofa002Id ?? "", 
				efa004Id: paramsOnPost.current.efa004Id ?? "", 
				efa005Id: paramsOnPost.current.efa005Id ?? "", 
				type
			}
		});
		addToast({
			title: translate("Sucesso"),
			description: translate("Permissões excluídas com sucesso"),
			color: "green"
		});					
	};

	const submitDelete = () => {
		confirm(
			translate("Exclusão de permissões"), 
			`${translate("Deseja realmente excluir as permissões")}?`,
			async () => {
				await deleteAllFromHierarchy();
				await getHierarchy(paramsOnPost.current.ofa002Id, paramsOnPost.current.efa004Id, paramsOnPost.current.efa005Id);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir permissões"), addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		openModalPermissionsTotalFinancialManagement
	}));

	return (
		<Modal
			ref={modalPermissions}
			header={{
				title: translate("Permissões"),
				icon: {
					name: "lock"
				},
				subtitle: `${getTitleBudgetTypes(type, translate)  }: ${  translate("Total")}`
			}}
			footer={{
				buttonLoadingText: translate("Carregando"),
				saveButtonText: translate("Sim"),
				cancelButtonText: translate("Cancelar"),
				showSaveButton: false
			}}
			withoutForm
			styleInline={ModalSide}
			onClose={() => {
				onCallbackFunction.current();
			}}
		>
			<Title color="grey" size="xs" marginBottom={8}>
				{translate("Utilize as permissões para definir a unidade/filial/usuário/grupo de usuários e sua permissão diretamente na hierarquia")}
			</Title>
			<LovSubsidiary 
				control={control}
				name="subsidiary"
				label={translate("Unidade/Filial")}
				extraParametersOnLoad={{
					budgetPlanId
				}}
				onChangeCapture={(e: any) => {
					getHierarchy(e.value, undefined, undefined);
					reset({ 
						group: {value: "", label: ""}, 
						user: {value: "", label: ""}
					});
				}}
			/>
			<LovUsers 
				control={control}
				name="user"
				label={translate("Usuário")}
				onChangeCapture={(e: any) => {
					getHierarchy(undefined, undefined, e.value);
					reset({ 
						group: {value: "", label: ""}, 
						subsidiary: {value: "", label: ""}
					});
				}}
			/>
			<LovUsersGroups 
				control={control}
				name="group"
				label={translate("Grupo de usuários")}
				onChangeCapture={(e: any) => {
					getHierarchy(undefined, e.value, undefined);
					reset({ 
						user: {value: "", label: ""}, 
						subsidiary: {value: "", label: ""}
					});
				}}
			/>
			<RCCGrid container item alignItems="center" justifyContent="space-between">
				<RCCSwitch
					name="filter"
					register={register}
					label="Preview"
					defaultChecked={false}
					onChangeCapture={(e: any) => {
						filterSwitch.current = e.target.checked;
						getHierarchy(paramsOnPost.current.ofa002Id, paramsOnPost.current.efa004Id, paramsOnPost.current.efa005Id);
					}}
				/>
				{hierarchy && hierarchy.length > 0 && (
					<RCCTooltip
						color="red"
						text={translate("Excluir todos")}
					>
						<RCCButton 
							color="red"
							layout="isRounded"
							size="md"
							icon={{
								name: "trash"
							}}
							onClick={() => submitDelete()}
						/>
					</RCCTooltip>
				)}
			</RCCGrid>
			<br />
			<>
				{loadingHierarchy ? <Loader /> : (
					<>
						{hierarchy && hierarchy.length === 0 ? (
							<RCCAlert
								title={translate("Atenção")}
								description={translate("Selecione uma opção para carregar a hierarquia")}
							/>
						): (
							<RCCGrid container item justifyContent="flex-end">
								<RCCButton 
									color="transparent"
									icon={{
										name: "search",
										color: "var(--default-color-font)",
										size: 16
									}}
									onClick={() => refHierarchicalTree.current?.toggleSearch()}
								/>
								<RCCHierarchicalTree
									data={hierarchy}
									styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 160px; }"
									ref={refHierarchicalTree}
									isSearchable={{
										searchKeyOfNode: "name",
										placeholderInput: translate("Pesquisar")
									}}
									getNodeKey={getNodeKeyFunction}
									onChange={(treeData) => setHierarchy(treeData)}
									generateNodeProps={(row:any) => ({
										title: <HierarchyNode 
											node={row.node}
											path={row.path} 
											treeIndex={row.treeIndex} 
											lowerSiblingCounts={row.lowerSiblingCounts} 
											isSearchMatch={row.isSearchMatch}
											isSearchFocus={row.isSearchFocus}
											onSubmit={onSubmit}
											control={controlPermission}
										/>
									})}
									canDrag={() => false}
									canDrop={() => false}
								/>
							</RCCGrid>
						)}
					</>
				)}
			</>
		</Modal>
	);
};

export default React.forwardRef(ModalPermissionsTotalFinancialManagement);
